import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  Timestamp,
  updateDoc,
} from "@firebase/firestore";
import { storeToRefs } from "pinia";
import { SubmittedDocument } from "types/SubmittedDocument";
import { DocumentState } from "~/types/enums/DocumentState";
import { Revision } from "~/types/Revision";
import { isSubmittedComparisonDocument, isSubmittedComparisonRevision } from "~/utils/document-comparison";

import debounce from "lodash.debounce";
import { DocumentViewMode } from "types/enums/DocumentViewMode.enum";
import { StudentLinkedAccounts } from "~/classes/models/studentLinkedAccounts/student-linked-account.model";
import { StudentNote } from "~/classes/models/students/student-notes.model";
import { Student } from "~/classes/models/students/student.model";
import { StudentsService } from "~/classes/models/students/students.service";

const studentEditingDocumentStates = [
  DocumentState.draft,
  DocumentState.editing,
  DocumentState.studentReview,
  DocumentState.studentCompleted,
];

const shouldUseStudentSubmissionRevision = (
  submittedDocument: SubmittedDocument,
  viewMode: DocumentViewMode
) => {
  if ([DocumentViewMode.teacher, DocumentViewMode.compare].includes(viewMode)) {
    return false;
  }

  return studentEditingDocumentStates.includes(submittedDocument.state);
};

export const useSubmittedDocument = (documentId: string) =>
  defineStore(`/submitted/documents/${documentId}`, () => {
    const documentId = ref<string>("");
    const revision = ref<Revision>();
    const student = ref<Student | undefined>();
    const studentLinkedAccounts = ref<StudentLinkedAccounts>([]);
    const studentNote = ref<StudentNote>();
    const submittedDocument = ref<SubmittedDocument>();

    const selectedTestMetric = ref<string | undefined>(undefined);

    const isDocumentInitialized = ref<boolean>(false);

    const restoreRecoveryChecklist = async () => {
      const document = submittedDocument.value;
      if (revision.value?.id !== "recovered-draft" || !document?.checklistId || !document.checklistPath) return;
      const checklists = await getDocs(collection(
        useFirestore(), `/documents/${document.id}/revisions/${revision.value.id}/checklists`
      ));
      if (checklists.empty) {
        await useApplyChecklistToDocuments().applyChecklistToDocumentNow(
          document.checklistId, document.checklistPath, document.id!, false
        );
      }
    };

    const initialize = async (docId: string, mode: DocumentViewMode) => {
      documentId.value = docId;
      const db = useFirestore();
      const viewMode = mode;

      const documentViewModeStore = useDocumentViewMode(docId)();
      documentViewModeStore.setDocumentViewMode(viewMode);

      const submittedDocumentRef = doc(db, `/documents/${docId}`);
      const submittedDocumentDoc = await getDoc(submittedDocumentRef);
      submittedDocument.value = {
        ...submittedDocumentDoc.data(),
        id: submittedDocumentDoc.id,
      } as SubmittedDocument;

      if (viewMode === DocumentViewMode.compare && !isSubmittedComparisonDocument(submittedDocument.value)) {
        throw new Error("This document has not been submitted to the teacher.");
      }

      if (
        viewMode == DocumentViewMode.teacher &&
        submittedDocument.value.startGradingAtTimestamp == undefined
      ) {
        await runTransaction(db, async transaction => {
          const snapshot = await transaction.get(submittedDocumentRef);
          const current = snapshot.data() as SubmittedDocument;
          if (studentEditingDocumentStates.includes(current.state)) {
            throw new Error("This submission has been withdrawn for editing.");
          }
          const changes = {
            startGradingAtTimestamp: current.startGradingAtTimestamp ?? Date.now(),
            state: current.state === DocumentState.submitted ? DocumentState.grading : current.state,
          };
          transaction.update(submittedDocumentRef, changes);
          submittedDocument.value = { ...current, ...changes, id: snapshot.id };
        });
      }

      if (
        submittedDocument.value.studentId &&
        submittedDocument.value.classroomId
      ) {
        try {
          student.value = await StudentsService.get(
            submittedDocument.value.studentId
          );

          StudentsService.streamStudentLinkedAccounts(
            submittedDocument.value.studentId,
            submittedDocument.value.classroomId
          ).subscribe((data) => {
            studentLinkedAccounts.value = data;
          });
        } catch (error) {
          // empty
        }

        try {
          studentNote.value = await StudentsService.fetchStudentNote({
            studentId: submittedDocument.value.studentId,
          });
        } catch (error) {
          // empty
        } finally {
          if (
            studentNote.value == undefined &&
            submittedDocument.value.classroomId
          ) {
            studentNote.value = StudentNote.default({
              teacherId: submittedDocument.value.userId,
              studentId: submittedDocument.value.studentId,
              classroomId: submittedDocument.value.classroomId,
            });
          }
        }

        isDocumentInitialized.value = true;
      }

      const revisionsRef = collection(db, `/documents/${docId}/revisions`);
      const revisionsQuery = query(
        revisionsRef,
        orderBy("lastUpdatedTimestamp", "desc")
      );
      let response = await getDocs(revisionsQuery);
      // orderBy excludes older records without a timestamp. Check those before
      // deciding that there is no saved content to recover.
      if (response.docs.length === 0) {
        response = await getDocs(revisionsRef);
      }
      if (response.docs.length == 0) {
        if (viewMode === DocumentViewMode.compare) {
          throw new Error("This document has no submitted revision to compare.");
        }
        if (
          viewMode === DocumentViewMode.student &&
          [DocumentState.draft, DocumentState.editing].includes(submittedDocument.value.state)
        ) {
          // A fixed ID and transaction let simultaneous opens reuse the same
          // recovery draft without overwriting anything already saved there.
          const recoveryRef = doc(revisionsRef, "recovered-draft");
          revision.value = await runTransaction(db, async transaction => {
            const currentDocument = await transaction.get(submittedDocumentRef);
            const recoveredDraft = await transaction.get(recoveryRef);
            if (![DocumentState.draft, DocumentState.editing].includes(currentDocument.data()?.state)) {
              throw new Error("This document is no longer editable. Please reload it.");
            }
            if (recoveredDraft.exists()) {
              return { ...recoveredDraft.data(), id: recoveredDraft.id } as Revision;
            }
            const blankDraft = {
              documentId: docId,
              externalId: docId,
              lastUpdatedTimestamp: Date.now(),
              paragraphs: [],
              studentSubmissionOps: [{ insert: "\n" }],
            };
            transaction.set(recoveryRef, blankDraft);
            return { ...blankDraft, id: recoveryRef.id } as Revision;
          });
          await restoreRecoveryChecklist();
          return;
        }
        throw new Error("This document has no saved revision. Please create a new document or upload your file again.");
      }

      const useStudentSubmissionRevision = shouldUseStudentSubmissionRevision(
        submittedDocument.value,
        viewMode
      );
      const relevantRevisions = response.docs.filter((d) => {
        if (d.data().isArchived === true) return false;
        const hasStudentSubmissionOps =
          d.data().studentSubmissionOps != undefined;

        return useStudentSubmissionRevision
          ? hasStudentSubmissionOps
          : !hasStudentSubmissionOps;
      });

      if (relevantRevisions.length == 0 && response.docs.length == 0) {
        return null;
      }

      const revisionDoc = viewMode === DocumentViewMode.compare
        ? response.docs.find(d => isSubmittedComparisonRevision(d.data()))
        : relevantRevisions[0] ?? response.docs.find(d => d.data().isArchived !== true);
      if (!revisionDoc && viewMode === DocumentViewMode.compare) {
        throw new Error("This document has no submitted revision to compare.");
      }
      if (!revisionDoc) return null;
      revision.value = revisionDoc.data() as Revision;
      revision.value.id = revisionDoc.id;
      await restoreRecoveryChecklist();

      if (revision.value.importedChecklist == undefined) {
        const revisionWithImportedChecklist = response.docs.find(
          (document) => document.data().isArchived !== true && document.data().importedChecklist != undefined
        );

        revision.value.importedChecklist =
          revisionWithImportedChecklist?.data().importedChecklist;
      }

      if (
        submittedDocument.value.state == DocumentState.submitted &&
        viewMode == DocumentViewMode.teacher
      ) {
        await updateDocumentState(DocumentState.grading);
      }
    };

    const saveRevision = async (
      revision: Revision,
      saveDocumentEditingHistory: boolean = false
    ) => {
      const { save, saved } = useDocumentSavingStatusStore(documentId.value);
      save();
      const db = useFirestore();
      const revisionRef = doc(
        db,
        `/documents/${documentId.value}/revisions/${revision.id}`
      );

      const revisionData: any = {
        ...revision,
      };

      for (var field in revisionData) {
        if (revisionData[field] == undefined) {
          revisionData[field] = deleteField();
        }
      }

      await updateDoc(revisionRef, revisionData);
      saved();
    };

    const saveDocument = async () => {
      if (!submittedDocument.value) return;

      const db = useFirestore();
      const submittedDocumentRef = doc(db, `/documents/${documentId.value}`);
      await updateDoc(submittedDocumentRef, submittedDocument.value);
    };

    const saveDocumentDebounced = debounce(saveDocument, 500);

    const updateDocumentState = async (state: DocumentState) => {
      const documentChecklistStore = useDocumentChecklistStore(
        documentId.value
      );
      const { useTotalScore, useTotalEarnedScore } = storeToRefs(
        documentChecklistStore
      );

      const db = useFirestore();
      const submittedDocumentRef = doc(db, `/documents/${documentId.value}`);

      const docData: any = {
        state: state,
      };

      if (state === DocumentState.submitted) {
        docData.submittedAtTimestamp = Timestamp.now();
        docData.submittedAtTimestampMillis = docData.submittedAtTimestamp.toMillis();
        docData.isLate = submittedDocument.value?.assignmentDueTimestamp != null &&
          docData.submittedAtTimestampMillis > submittedDocument.value.assignmentDueTimestamp;
      }

      if (state == DocumentState.graded) {
        docData["earnedPoints"] = useTotalEarnedScore.value;
        docData["maxPoints"] = useTotalScore.value;
        docData["finishedGradingAtTimestamp"] = new Date().getTime();
      }

      if (submittedDocument.value) {
        Object.assign(submittedDocument.value, docData);
      }

      await updateDoc(submittedDocumentRef, docData);
    };

    const documentLevel = computed(() => {
      return submittedDocument.value?.levelId;
    });

    const documentLesson = computed(() => {
      return submittedDocument.value?.lessonId;
    });

    const showGeneralCommentForm = ref<boolean>(false);

    const hasContacts = computed(() => {
      if (student.value == undefined) return false;

      return studentLinkedAccounts.value.length > 0;
    });

    const hasChecklist = computed(() => {
      return submittedDocument.value?.checklistId != undefined;
    });

    const isEditingMode = computed(() => {
      if (useDocumentViewMode(documentId.value)().isCompareMode) return false;
      var editing = [
        DocumentState.draft,
        DocumentState.editing,
        DocumentState.studentReview,
        DocumentState.studentCompleted,
      ].includes(submittedDocument.value?.state ?? DocumentState.submitted);

      if (editing != true) {
        return false;
      }

      // Next we need to see waht kind of submisison we have.
      if (
        revision.value?.fileObject != undefined ||
        (revision.value?.fileObjects != undefined &&
          revision.value?.fileObjects.length > 0)
      ) {
        return false;
      }

      return true;
    });

    const setIsEditingMode = async (isEditing: boolean) => {
      if (submittedDocument.value) {
        const newState = isEditing
          ? DocumentState.editing
          : DocumentState.studentReview;
        await updateDocumentState(newState);
      }
    };

    return {
      documentId,
      submittedDocument,
      revision,
      documentLevel,
      documentLesson,
      showGeneralCommentForm,
      initialize,
      saveDocument,
      saveDocumentDebounced,
      saveRevision,
      updateDocumentState,
      isDocumentInitialized,
      // Student info
      student,
      studentLinkedAccounts,
      studentNote,
      hasContacts,
      hasChecklist,
      selectedTestMetric,
      isEditingMode,
      setIsEditingMode,
    };
  });
